import { useEffect, useMemo, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import { normalizeForSearch } from "../../util/StringUtils";

export interface SearchableOption {
  value: number | string;
  label: string;
  /** Optional richer label shown in the dropdown row (label kept as fallback / committed text). */
  secondary?: string | null;
  /** Additional text to include when matching search queries (e.g. player names for a user picker). */
  searchTokens?: string[];
}

interface SearchableSelectProps {
  options: SearchableOption[];
  value: number | string | "";
  onChange: (value: number | string | "") => void;
  placeholder?: string;
  emptyPlaceholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Lightweight typeahead select. The input holds either the free-text query (while searching)
 * or the label of the picked option (once committed). We keep them separate so the caller
 * still receives the raw option `value` — matching the surrounding form contract.
 */
function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "",
  emptyPlaceholder = "—",
  className,
  disabled = false,
}: SearchableSelectProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);

  const selectedLabel = useMemo(
    () => options.find((o) => String(o.value) === String(value))?.label ?? "",
    [options, value],
  );

  // Reset the query whenever the caller-controlled `value` changes; the input then shows the
  // committed label instead of a stale search string.
  useEffect(() => {
    if (!open) setQuery("");
  }, [value, open]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = normalizeForSearch(query.trim());
    if (!q) return options;
    return options.filter((o) => {
      const haystack = [o.label, o.secondary ?? "", ...(o.searchTokens ?? [])]
        .filter(Boolean)
        .map(normalizeForSearch)
        .join(" ");
      return haystack.includes(q);
    });
  }, [options, query]);

  useEffect(() => {
    if (highlightIndex >= filtered.length) setHighlightIndex(0);
  }, [filtered, highlightIndex]);

  const commit = (opt: SearchableOption) => {
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  };

  const displayed = open ? query : selectedLabel;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlightIndex];
      if (opt) commit(opt);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    } else if (e.key === "Backspace" && !query && value !== "") {
      // Backspace on committed value clears the selection and reopens for a fresh search.
      onChange("");
      setOpen(true);
    }
  };

  return (
    <div ref={containerRef} className={`searchable-select ${className ?? ""}`}>
      <Form.Control
        type="text"
        value={displayed}
        placeholder={value === "" ? emptyPlaceholder : placeholder}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setOpen(true);
          setQuery(e.target.value);
          setHighlightIndex(0);
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {open && filtered.length > 0 ? (
        <ul className="searchable-select-menu" role="listbox">
          {filtered.map((opt, idx) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={idx === highlightIndex}
              className={
                "searchable-select-option" +
                (idx === highlightIndex ? " is-active" : "") +
                (String(opt.value) === String(value) ? " is-selected" : "")
              }
              onMouseDown={(e) => {
                // mousedown, not click — click fires after blur, which would close the menu first.
                e.preventDefault();
                commit(opt);
              }}
              onMouseEnter={() => setHighlightIndex(idx)}
            >
              <span>{opt.label}</span>
              {opt.secondary ? (
                <span className="searchable-select-secondary">{opt.secondary}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {open && filtered.length === 0 ? (
        <ul className="searchable-select-menu">
          <li className="searchable-select-empty">—</li>
        </ul>
      ) : null}
    </div>
  );
}

export default SearchableSelect;
